import { logger } from '#~/logger/index.js';

type Fields = Record<string, string | number | boolean | undefined>;
const recent = new Map<
  string,
  { agentId: string; expires: number; snapshots: Map<string, string> }
>();

/** IDs, flags and timings only: never log camera configuration or raw errors. */
export const traceCameraRemove = (event: string, fields: Fields = {}): void => {
  try {
    logger.info(
      `[camera-remove-trace] ${JSON.stringify({ event, at: new Date().toISOString(), ...fields })}`
    );
  } catch {
    // Diagnostics must not change removal behavior.
  }
};

export const watchCameraRemoval = (deviceId: string, agentId: string): void => {
  const previous = recent.get(deviceId);
  recent.set(deviceId, {
    agentId,
    expires: Date.now() + 60_000,
    snapshots: previous?.snapshots ?? new Map()
  });
  // Bound retained identity even if many cameras are removed without state updates.
  if (recent.size > 256) recent.delete(recent.keys().next().value!);
};

/** Emit only changed membership during the minute following a deletion. */
export const observeCameraRemoval = (
  agentId: string,
  source: string,
  membership: (deviceId: string) => Fields,
  flags: Fields = {}
): void => {
  for (const [deviceId, entry] of recent) {
    if (entry.expires < Date.now()) {
      recent.delete(deviceId);
      continue;
    }
    if (entry.agentId !== agentId) continue;
    const fields = membership(deviceId);
    const snapshot = JSON.stringify({
      ...fields,
      authority: flags.authority,
      optimistic: flags.optimistic
    });
    if (entry.snapshots.get(source) === snapshot && !flags.reset && !flags.resync) continue;
    entry.snapshots.set(source, snapshot);
    traceCameraRemove(source, { deviceId, agentId, ...fields, ...flags });
  }
};
